import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Link2, Shield, Mail, Store as StoreIcon, Save, Image as ImageIcon, Upload, Globe, CalendarClock } from "lucide-react";
import { adminApi, formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const Section = ({ icon: Icon, title, desc, children }) => (
  <div className="bg-white border border-[#E2DEC9] rounded-xl p-6">
    <div className="flex items-center gap-2 mb-1"><Icon className="h-4 w-4 text-[#C85A32]" /><h3 className="font-serif text-lg text-[#1A1918]">{title}</h3></div>
    {desc && <p className="text-sm text-[#5C5852] mb-4">{desc}</p>}
    {children}
  </div>
);

const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4), m = (i % 4) * 15;
  const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  const label = `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
  return { value, label };
});

const DailySummarySection = ({ value, onChange }) => {
  const [sending, setSending] = useState(false);
  const [last, setLast] = useState(null);
  useEffect(() => {
    adminApi.get("/daily-summary/status").then(({ data }) => setLast(data.last)).catch(() => {});
  }, []);
  const set = (k, v) => onChange({ ...value, [k]: v });
  const sendNow = async () => {
    if (!(value.recipient || "").trim()) { toast.error("Enter a recipient email"); return; }
    setSending(true);
    try {
      const { data } = await adminApi.post("/daily-summary/send-now", { recipient: value.recipient.trim() });
      toast.success(`Summary sent to ${value.recipient.trim()} (${data.count} order${data.count !== 1 ? "s" : ""} from ${data.day})`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <Section icon={CalendarClock} title="Daily Summary Email" desc="Each day at the time below (Eastern Time), email a table of every order whose design was finalized the previous day, with a Download & Print link that opens the dashboard with those orders pre-selected. No email is sent on days with zero finalized designs.">
      <div className="space-y-3" data-testid="daily-summary-section">
        <div className="flex items-center justify-between gap-4 border-b border-[#EFEADF] pb-3">
          <div><p className="text-sm text-[#1A1918]">Send daily summary</p><p className="text-xs text-[#8C867B]">Subject: “CraftPort: [X] Orders Ready For Print Today”</p></div>
          <Switch checked={!!value.enabled} onCheckedChange={(v) => set("enabled", v)} className="data-[state=checked]:bg-[#C85A32]" data-testid="daily-summary-enabled-switch" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs text-[#5C5852]">Recipient email</Label><Input type="email" value={value.recipient || ""} onChange={(e) => set("recipient", e.target.value)} placeholder="print@yourbrand.com" className="bg-white border-[#C8C2AC] mt-1" data-testid="daily-summary-recipient-input" /></div>
          <div>
            <Label className="text-xs text-[#5C5852]">Send time (Eastern Time)</Label>
            <select value={value.send_time || "08:00"} onChange={(e) => set("send_time", e.target.value)} className="h-10 w-full mt-1 rounded-md border border-[#C8C2AC] bg-white px-3 text-sm" data-testid="daily-summary-time-select">
              {TIME_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label} ET</option>)}
            </select>
          </div>
        </div>
        <p className="text-[11px] text-[#8C867B]" data-testid="daily-summary-last-status">
          {last ? `Last scheduled run: ${last.date} · ${last.status}${last.count != null ? ` · ${last.count} order${last.count !== 1 ? "s" : ""}` : ""}` : "No scheduled summary has run yet."} Changes apply after you click Save settings.
        </p>
        <div className="border-t border-[#EFEADF] pt-3 flex flex-col sm:flex-row sm:items-center gap-2 justify-between">
          <p className="text-[11px] text-[#8C867B]">Send yesterday's summary right now to the recipient above — sends even with 0 orders so you can preview it.</p>
          <Button onClick={sendNow} disabled={sending} variant="outline" className="border-[#C85A32] text-[#C85A32] hover:bg-[#C85A32]/10 whitespace-nowrap" data-testid="daily-summary-send-now-btn">
            {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />} Send summary now
          </Button>
        </div>
      </div>
    </Section>
  );
};

export default function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [settings, setSettings] = useState(null);
  const [newMap, setNewMap] = useState({ tag: "", template_id: "" });
  const [newStore, setNewStore] = useState({ name: "", shop_domain: "", webhook_secret: "" });
  const [testTo, setTestTo] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [heroBusy, setHeroBusy] = useState(false);
  const heroInputRef = useRef(null);
  const [ogBusy, setOgBusy] = useState(false);
  const ogInputRef = useRef(null);
  const [faviconBusy, setFaviconBusy] = useState(false);
  const faviconInputRef = useRef(null);

  const load = async () => {
    try {
      const [p, s, st] = await Promise.all([adminApi.get("/products"), adminApi.get("/settings"), adminApi.get("/stores")]);
      setProducts(p.data); setStores(st.data);
      let sdata = s.data;
      // Auto-capture this (public) app origin as the email link base when unset,
      // so webhook-generated links never fall back to the internal cluster host (403).
      if (!(sdata.portal_base_url || "").trim() && typeof window !== "undefined") {
        const origin = window.location.origin;
        try {
          const { data } = await adminApi.put("/settings", {
            mappings: sdata.mappings, privacy: sdata.privacy, email: sdata.email, portal_base_url: origin, portal: sdata.portal, seo: sdata.seo,
          });
          sdata = data;
        } catch (_) { /* non-blocking */ }
      }
      setSettings(sdata);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const tplName = (id) => products.find((p) => p.id === id)?.name || "(unknown template)";

  const persistMappings = async (mappings) => {
    const next = { ...settings, mappings };
    setSettings(next);
    try {
      const { data } = await adminApi.put("/settings", {
        mappings, privacy: next.privacy, email: next.email, portal_base_url: next.portal_base_url || "", portal: next.portal, seo: next.seo,
      });
      setSettings(data);
    } catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const addMapping = () => {
    if (!newMap.tag.trim() || !newMap.template_id) { toast.error("Enter a tag and choose a template"); return; }
    persistMappings([...settings.mappings, { tag: newMap.tag.trim(), template_id: newMap.template_id }]);
    setNewMap({ tag: "", template_id: "" });
    toast.success("Mapping saved");
  };
  const removeMapping = (i) => persistMappings(settings.mappings.filter((_, idx) => idx !== i));
  const setPrivacy = (k, v) => setSettings((s) => ({ ...s, privacy: { ...s.privacy, [k]: v } }));
  const setEmail = (k, v) => setSettings((s) => ({ ...s, email: { ...s.email, [k]: v } }));
  const setPortal = (k, v) => setSettings((s) => ({ ...s, portal: { ...s.portal, [k]: v } }));
  const setSeo = (k, v) => setSettings((s) => ({ ...s, seo: { ...s.seo, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi.put("/settings", {
        mappings: settings.mappings, privacy: settings.privacy, email: settings.email, portal_base_url: settings.portal_base_url || "", portal: settings.portal, seo: settings.seo, daily_summary: settings.daily_summary,
      });
      setSettings(data); toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testTo.trim()) { toast.error("Enter an email address"); return; }
    setSendingTest(true);
    try {
      await adminApi.post("/settings/test-email", { to: testTo.trim(), email: settings.email, portal_base_url: settings.portal_base_url || window.location.origin });
      toast.success(`Test email sent to ${testTo.trim()}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingTest(false); }
  };

  const addStore = async () => {
    if (!newStore.name.trim() || !newStore.shop_domain.trim()) { toast.error("Store name and domain are required"); return; }
    try {
      await adminApi.post("/stores", newStore);
      setNewStore({ name: "", shop_domain: "", webhook_secret: "" });
      toast.success("Store added");
      const st = await adminApi.get("/stores"); setStores(st.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteStore = async (id) => {
    try { await adminApi.delete(`/stores/${id}`); setStores((v) => v.filter((s) => s.id !== id)); toast.success("Store removed"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadHero = async (file) => {
    if (!file) return;
    setHeroBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await adminApi.post("/settings/portal-hero", fd);
      toast.success("Portal login image updated");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setHeroBusy(false); if (heroInputRef.current) heroInputRef.current.value = ""; }
  };
  const removeHero = async () => {
    setHeroBusy(true);
    try { await adminApi.delete("/settings/portal-hero"); toast.success("Portal login image removed"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setHeroBusy(false); }
  };

  const uploadOg = async (file) => {
    if (!file) return;
    setOgBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await adminApi.post("/settings/og-image", fd);
      toast.success("Social sharing image updated");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOgBusy(false); if (ogInputRef.current) ogInputRef.current.value = ""; }
  };
  const removeOg = async () => {
    setOgBusy(true);
    try { await adminApi.delete("/settings/og-image"); toast.success("Social sharing image removed"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setOgBusy(false); }
  };

  const uploadFavicon = async (file) => {
    if (!file) return;
    setFaviconBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await adminApi.post("/settings/favicon", fd);
      toast.success("Favicon updated");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setFaviconBusy(false); if (faviconInputRef.current) faviconInputRef.current.value = ""; }
  };
  const removeFavicon = async () => {
    setFaviconBusy(true);
    try { await adminApi.delete("/settings/favicon"); toast.success("Favicon removed"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setFaviconBusy(false); }
  };

  if (loading || !settings) return <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#C85A32]" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-settings">
      {/* Mapping manager */}
      <Section icon={Link2} title="Product → Template Mapping" desc="Enter a word from the Shopify product title (e.g. &quot;Custom Pouch&quot;) and link it to a design template. Incoming order line items whose title contains this text auto-generate a secure design slot.">
        <div className="space-y-2 mb-4">
          {settings.mappings.length === 0 && <p className="text-xs text-[#8C867B]">No mappings yet.</p>}
          {settings.mappings.map((m, i) => (
            <div key={i} className="flex items-center gap-3 bg-[#F8F6F0] border border-[#E2DEC9] rounded-md px-3 py-2" data-testid={`mapping-row-${i}`}>
              <span className="font-mono text-xs bg-[#C85A32]/12 text-[#C85A32] px-2 py-1 rounded">{m.tag}</span>
              <span className="text-[#8C867B]">→</span>
              <span className="text-sm text-[#1A1918] flex-1">{tplName(m.template_id)}</span>
              <button onClick={() => removeMapping(i)} className="text-[#B0413E] hover:opacity-70" data-testid={`mapping-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input placeholder="Product title keyword (e.g. Custom Pouch)" value={newMap.tag} onChange={(e) => setNewMap((v) => ({ ...v, tag: e.target.value }))} className="bg-white border-[#C8C2AC]" data-testid="mapping-tag-input" />
          <select value={newMap.template_id} onChange={(e) => setNewMap((v) => ({ ...v, template_id: e.target.value }))} className="h-10 rounded-md border border-[#C8C2AC] bg-white px-3 text-sm min-w-[200px]" data-testid="mapping-template-select">
            <option value="">Choose template…</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <Button onClick={addMapping} className="bg-[#C85A32] hover:bg-[#b04e2b]" data-testid="mapping-add-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
        </div>
      </Section>

      {/* Privacy */}
      <Section icon={Shield} title="Customer Privacy" desc="Choose which customer details are shown in the design portal. Email is always required for delivery but never displayed. When name fields are off, an internal order hash is shown instead.">
        <div className="space-y-3">
          {[
            ["show_full_name", "Show full name", "Display the customer's full name in the portal."],
            ["show_first_name", "Show first name only", "Casual and private — first name only."],
            ["show_order_number", "Show Shopify order number", "Expose the original Shopify order number in the portal session."],
          ].map(([k, label, hint]) => (
            <div key={k} className="flex items-center justify-between gap-4 border-b border-[#EFEADF] pb-3">
              <div><p className="text-sm text-[#1A1918]">{label}</p><p className="text-xs text-[#8C867B]">{hint}</p></div>
              <Switch checked={!!settings.privacy[k]} onCheckedChange={(v) => setPrivacy(k, v)} className="data-[state=checked]:bg-[#C85A32]" data-testid={`privacy-${k}`} />
            </div>
          ))}
        </div>
      </Section>

      {/* Email template */}
      <Section icon={Mail} title="Portal Link Email" desc="Customize the email sent to customers with their secure per-item design link.">
        <div className="space-y-3">
          <div className="rounded-md bg-[#F3F0E8] border border-[#E2DEC9] px-3 py-2">
            <p className="text-[11px] text-[#5C5852] leading-relaxed">The <strong>sender email address</strong> is managed by the platform and can't be changed. You can set the <strong>display name</strong> shown in the inbox, and a <strong>reply-to address</strong> so customer replies land in your own inbox.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs text-[#5C5852]">Sender display name</Label><Input value={settings.email.from_name || ""} onChange={(e) => setEmail("from_name", e.target.value)} placeholder="Your brand name" className="bg-white border-[#C8C2AC] mt-1" data-testid="email-from-name-input" /></div>
            <div><Label className="text-xs text-[#5C5852]">Reply-to email</Label><Input type="email" value={settings.email.reply_to || ""} onChange={(e) => setEmail("reply_to", e.target.value)} placeholder="replies@yourbrand.com" className="bg-white border-[#C8C2AC] mt-1" data-testid="email-reply-to-input" /></div>
          </div>
          <div><Label className="text-xs text-[#5C5852]">Subject</Label><Input value={settings.email.subject} onChange={(e) => setEmail("subject", e.target.value)} className="bg-white border-[#C8C2AC] mt-1" data-testid="email-subject-input" /></div>
          <div><Label className="text-xs text-[#5C5852]">Body text</Label><Textarea value={settings.email.intro} onChange={(e) => setEmail("intro", e.target.value)} rows={3} className="bg-white border-[#C8C2AC] mt-1" data-testid="email-intro-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-[#5C5852]">Button label</Label><Input value={settings.email.button_label} onChange={(e) => setEmail("button_label", e.target.value)} className="bg-white border-[#C8C2AC] mt-1" data-testid="email-button-label-input" /></div>
            <div>
              <Label className="text-xs text-[#5C5852]">Button color</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(settings.email.button_color) ? settings.email.button_color : "#C85A32"} onChange={(e) => setEmail("button_color", e.target.value)} className="h-9 w-12 rounded border border-[#C8C2AC] bg-white" data-testid="email-button-color-input" />
                <Input value={settings.email.button_color} onChange={(e) => setEmail("button_color", e.target.value)} className="bg-white border-[#C8C2AC] font-mono" />
              </div>
            </div>
          </div>
          <div><Label className="text-xs text-[#5C5852]">Footer note</Label><Textarea value={settings.email.footer} onChange={(e) => setEmail("footer", e.target.value)} rows={2} className="bg-white border-[#C8C2AC] mt-1" data-testid="email-footer-input" /></div>
          <div><Label className="text-xs text-[#5C5852]">Portal base URL (link origin)</Label><Input value={settings.portal_base_url || ""} onChange={(e) => setSettings((s) => ({ ...s, portal_base_url: e.target.value }))} placeholder="Leave blank to auto-detect" className="bg-white border-[#C8C2AC] mt-1 font-mono text-xs" data-testid="email-base-url-input" /></div>
          <div className="border-t border-[#EFEADF] pt-3">
            <Label className="text-xs text-[#5C5852]">Send a test email</Label>
            <p className="text-[11px] text-[#8C867B] mb-2">Previews the template above (including unsaved edits) using sample order #1010A.</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input type="email" placeholder="you@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="bg-white border-[#C8C2AC]" data-testid="test-email-input" />
              <Button onClick={sendTest} disabled={sendingTest} variant="outline" className="border-[#C85A32] text-[#C85A32] hover:bg-[#C85A32]/10 whitespace-nowrap" data-testid="test-email-send-btn">
                {sendingTest ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />} Send test
              </Button>
            </div>
          </div>
        </div>
      </Section>

      <DailySummarySection value={settings.daily_summary || { enabled: false, recipient: "", send_time: "08:00" }} onChange={(v) => setSettings((s) => ({ ...s, daily_summary: v }))} />

      {/* Portal login screen */}
      <Section icon={ImageIcon} title="Portal Login Screen" desc="Brand the customer design portal login page — the image, badge, headline and subtext shown to customers.">
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3">
            <div><Label className="text-xs text-[#5C5852]">Badge (top-left label)</Label><Input value={settings.portal?.badge || ""} onChange={(e) => setPortal("badge", e.target.value)} placeholder="Custom Shop" className="bg-white border-[#C8C2AC] mt-1" data-testid="portal-badge-input" /></div>
            <div><Label className="text-xs text-[#5C5852]">Headline</Label><Input value={settings.portal?.heading || ""} onChange={(e) => setPortal("heading", e.target.value)} placeholder="Welcome to the Design Studio" className="bg-white border-[#C8C2AC] mt-1" data-testid="portal-heading-input" /></div>
            <div><Label className="text-xs text-[#5C5852]">Subtext</Label><Textarea value={settings.portal?.subtext || ""} onChange={(e) => setPortal("subtext", e.target.value)} rows={2} placeholder="A short line under the headline." className="bg-white border-[#C8C2AC] mt-1" data-testid="portal-subtext-input" /></div>
          </div>
          <p className="text-[11px] text-[#8C867B]">Text changes save when you click <strong>Save settings</strong> below. The image below applies immediately.</p>
          <div className="flex flex-col sm:flex-row gap-4 items-start border-t border-[#EFEADF] pt-4">
            <div className="w-48 h-32 rounded-lg overflow-hidden border border-[#E2DEC9] bg-[#F3F0E8] flex items-center justify-center shrink-0" data-testid="portal-hero-preview">
              {settings.portal_hero_path
                ? <img src={`${API}/portal/hero?v=${encodeURIComponent(settings.portal_hero_path.slice(-12))}`} alt="Portal login" className="w-full h-full object-cover" />
                : <span className="text-xs text-[#8C867B] px-3 text-center">Default stock image in use</span>}
            </div>
            <div className="flex flex-col gap-2">
              <input ref={heroInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadHero(e.target.files?.[0])} data-testid="portal-hero-file-input" />
              <Button onClick={() => heroInputRef.current?.click()} disabled={heroBusy} className="bg-[#C85A32] hover:bg-[#b04e2b]" data-testid="portal-hero-upload-btn">
                {heroBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />} {settings.portal_hero_path ? "Replace image" : "Upload image"}
              </Button>
              {settings.portal_hero_path && (
                <Button onClick={removeHero} disabled={heroBusy} variant="outline" className="border-[#B0413E]/40 text-[#B0413E] hover:bg-[#B0413E]/10" data-testid="portal-hero-remove-btn">
                  <Trash2 className="h-4 w-4 mr-1" /> Remove
                </Button>
              )}
              <p className="text-[11px] text-[#8C867B] max-w-xs">Recommended: a tall/portrait photo at least 1200px wide (JPG, PNG, WEBP, or GIF, under 8 MB).</p>
            </div>
          </div>
        </div>
      </Section>

      {/* SEO & social sharing */}
      <Section icon={Globe} title="SEO & Social Sharing" desc="Control how your site appears in browser tabs, search results, and social link previews (Discord, iMessage, X/Twitter, Facebook).">
        <div className="space-y-4">
          <div><Label className="text-xs text-[#5C5852]">Page title</Label><Input value={settings.seo?.site_title || ""} onChange={(e) => setSeo("site_title", e.target.value)} placeholder="Wellsmade Co. Custom Studio" className="bg-white border-[#C8C2AC] mt-1" data-testid="seo-title-input" /></div>
          <div><Label className="text-xs text-[#5C5852]">Meta description / subheader</Label><Textarea value={settings.seo?.meta_description || ""} onChange={(e) => setSeo("meta_description", e.target.value)} rows={3} placeholder="A short sentence describing your store for search engines and social previews." className="bg-white border-[#C8C2AC] mt-1" data-testid="seo-description-input" /><p className="text-[11px] text-[#8C867B] mt-1">Aim for ~150–160 characters for best search display.</p></div>
          <div className="flex flex-col sm:flex-row gap-4 items-start border-t border-[#EFEADF] pt-4">
            <div className="w-48 h-[100px] rounded-lg overflow-hidden border border-[#E2DEC9] bg-[#F3F0E8] flex items-center justify-center shrink-0" data-testid="og-image-preview">
              {settings.og_image_path
                ? <img src={`${API}/seo/og-image?v=${encodeURIComponent(settings.og_image_path.slice(-12))}`} alt="Social preview" className="w-full h-full object-cover" />
                : <span className="text-xs text-[#8C867B] px-3 text-center">No social image set</span>}
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-xs text-[#5C5852]">Social sharing image (Open Graph)</Label>
              <input ref={ogInputRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => uploadOg(e.target.files?.[0])} data-testid="og-image-file-input" />
              <Button onClick={() => ogInputRef.current?.click()} disabled={ogBusy} className="bg-[#C85A32] hover:bg-[#b04e2b] w-fit" data-testid="og-image-upload-btn">
                {ogBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />} {settings.og_image_path ? "Replace image" : "Upload image"}
              </Button>
              {settings.og_image_path && (
                <Button onClick={removeOg} disabled={ogBusy} variant="outline" className="border-[#B0413E]/40 text-[#B0413E] hover:bg-[#B0413E]/10 w-fit" data-testid="og-image-remove-btn">
                  <Trash2 className="h-4 w-4 mr-1" /> Remove
                </Button>
              )}
              <p className="text-[11px] text-[#8C867B] max-w-xs">JPEG or PNG, recommended 1200×630px, under 8 MB. The image applies immediately; title/description save with the button below.</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 items-start border-t border-[#EFEADF] pt-4">
            <div className="w-16 h-16 rounded-lg overflow-hidden border border-[#E2DEC9] bg-[#F3F0E8] flex items-center justify-center shrink-0" data-testid="favicon-preview">
              {settings.favicon_path
                ? <img src={`${API}/favicon?v=${encodeURIComponent(settings.favicon_path.slice(-12))}`} alt="Favicon" className="w-8 h-8 object-contain" />
                : <span className="text-[10px] text-[#8C867B] text-center px-1">No icon</span>}
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-xs text-[#5C5852]">Browser tab icon (favicon)</Label>
              <input ref={faviconInputRef} type="file" accept=".ico,.png,.svg,image/png,image/svg+xml,image/x-icon" className="hidden" onChange={(e) => uploadFavicon(e.target.files?.[0])} data-testid="favicon-file-input" />
              <Button onClick={() => faviconInputRef.current?.click()} disabled={faviconBusy} className="bg-[#C85A32] hover:bg-[#b04e2b] w-fit" data-testid="favicon-upload-btn">
                {faviconBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />} {settings.favicon_path ? "Replace icon" : "Upload icon"}
              </Button>
              {settings.favicon_path && (
                <Button onClick={removeFavicon} disabled={faviconBusy} variant="outline" className="border-[#B0413E]/40 text-[#B0413E] hover:bg-[#B0413E]/10 w-fit" data-testid="favicon-remove-btn">
                  <Trash2 className="h-4 w-4 mr-1" /> Remove
                </Button>
              )}
              <p className="text-[11px] text-[#8C867B] max-w-xs">.ico, .png, or .svg, square (e.g. 32×32 or 512×512), under 1 MB. Applies immediately.</p>
            </div>
          </div>
          <div className="border-t border-[#EFEADF] pt-4" data-testid="seo-live-preview">
            <Label className="text-xs text-[#5C5852] mb-3 block">Live preview</Label>
            {/* Browser tab mock */}
            <div className="inline-flex items-center gap-2 bg-[#ECE7DC] rounded-t-md px-3 py-1.5 max-w-[280px] border border-[#D8D2C0] border-b-0">
              {settings.favicon_path
                ? <img src={`${API}/favicon?v=${encodeURIComponent(settings.favicon_path.slice(-12))}`} alt="" className="w-4 h-4 object-contain shrink-0" />
                : <span className="w-4 h-4 rounded-sm bg-[#C85A32] shrink-0" />}
              <span className="text-xs text-[#1A1918] truncate">{settings.seo?.site_title || "Page title"}</span>
              <span className="text-[#8C867B] ml-1 shrink-0">×</span>
            </div>
            {/* Social share card mock */}
            <div className="max-w-md rounded-lg overflow-hidden border border-[#D8D2C0] bg-white shadow-sm">
              <div className="aspect-[1200/630] bg-[#F3F0E8] flex items-center justify-center overflow-hidden">
                {settings.og_image_path
                  ? <img src={`${API}/seo/og-image?v=${encodeURIComponent(settings.og_image_path.slice(-12))}`} alt="Social preview" className="w-full h-full object-cover" />
                  : <span className="text-xs text-[#8C867B]">No social image — add one above</span>}
              </div>
              <div className="p-3 space-y-0.5">
                <div className="text-[10px] uppercase tracking-wide text-[#8C867B] truncate">{(settings.portal_base_url || "yourdomain.com").replace(/^https?:\/\//, "").replace(/\/$/, "")}</div>
                <div className="font-medium text-[#1A1918] text-sm truncate">{settings.seo?.site_title || "Page title"}</div>
                <div className="text-xs text-[#5C5852]" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{settings.seo?.meta_description || "Your meta description will appear here."}</div>
              </div>
            </div>
            <p className="text-[11px] text-[#8C867B] mt-2">Title & description update live as you type; images reflect your last upload. Social platforms show this after you re-publish.</p>
          </div>
        </div>
      </Section>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#C85A32] hover:bg-[#b04e2b]" data-testid="settings-save-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save settings
        </Button>
      </div>

      {/* Stores (multi-tenant) */}
      <Section icon={StoreIcon} title="Shopify Stores" desc="Register each store's webhook signing secret so orders/create webhooks can be verified. Secrets are encrypted and never shown again.">
        <div className="space-y-2 mb-4">
          {stores.length === 0 && <p className="text-xs text-[#8C867B]">No stores configured. The orders/create webhook is: <span className="font-mono">/api/webhooks/shopify/orders-create</span></p>}
          {stores.map((s) => (
            <div key={s.id} className="flex items-center gap-3 bg-[#F8F6F0] border border-[#E2DEC9] rounded-md px-3 py-2" data-testid={`store-row-${s.id}`}>
              <span className="text-sm text-[#1A1918] font-medium">{s.name}</span>
              <span className="font-mono text-xs text-[#8C867B]">{s.shop_domain}</span>
              <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-full ${s.has_secret ? "bg-[#2D6A4F]/12 text-[#2D6A4F]" : "bg-[#B0413E]/12 text-[#B0413E]"}`}>{s.has_secret ? "secret set" : "no secret"}</span>
              <button onClick={() => deleteStore(s.id)} className="ml-auto text-[#B0413E] hover:opacity-70" data-testid={`store-remove-${s.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <Input placeholder="Store name" value={newStore.name} onChange={(e) => setNewStore((v) => ({ ...v, name: e.target.value }))} className="bg-white border-[#C8C2AC]" data-testid="store-name-input" />
          <Input placeholder="shop.myshopify.com" value={newStore.shop_domain} onChange={(e) => setNewStore((v) => ({ ...v, shop_domain: e.target.value }))} className="bg-white border-[#C8C2AC]" data-testid="store-domain-input" />
          <Input placeholder="Webhook secret" type="password" value={newStore.webhook_secret} onChange={(e) => setNewStore((v) => ({ ...v, webhook_secret: e.target.value }))} className="bg-white border-[#C8C2AC]" data-testid="store-secret-input" />
          <Button onClick={addStore} className="bg-[#1A1918] hover:bg-black" data-testid="store-add-btn"><Plus className="h-4 w-4 mr-1" /> Add store</Button>
        </div>
      </Section>
    </div>
  );
}
